import { useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SvgIconComponent } from "@mui/icons-material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import InboxIcon from "@mui/icons-material/Inbox";
import Crop75Icon from "@mui/icons-material/Crop75";
import MarkunreadMailboxIcon from "@mui/icons-material/MarkunreadMailbox";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import BakeryDiningRoundedIcon from "@mui/icons-material/BakeryDiningRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/core/stores/useAuthStore";
import { useToastStore } from "@/core/stores/useToastStore";
import { useThemeStore } from "@/core/stores/useThemeStore";
import { useCurrentUser } from "@/features/auth/hooks/useCurrentUser";
import { useSubmittedInquiryCount } from "@/features/inquiries/hooks/useSubmittedInquiryCount";

interface NavItem {
  label: string;
  path: string;
  icon: SvgIconComponent;
}

const NAV_ITEMS: readonly NavItem[] = [
  { label: "Pantry", path: "/pantry", icon: Inventory2Icon },
  { label: "Packaging", path: "/packaging", icon: InboxIcon },
  { label: "Cake Pans", path: "/cake-pans", icon: Crop75Icon },
  { label: "Recipes", path: "/recipes", icon: MenuBookIcon },
  { label: "Inquiries", path: "/inquiries", icon: MarkunreadMailboxIcon },
  { label: "Orders", path: "/orders", icon: ReceiptLongIcon },
  { label: "Baking", path: "/baking", icon: SoupKitchenIcon },
  { label: "Settings", path: "/settings", icon: SettingsIcon },
];

const WIDTH_EXPANDED = 260;
const WIDTH_RAIL = 76;

interface Props {
  open?: boolean;
  onClose?: () => void;
  variant?: "permanent" | "temporary";
}

interface RowOptions {
  icon: SvgIconComponent;
  label: string;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  badgeCount?: number;
}

export function AppSidebar(props: Props): React.ReactElement {
  const { open = false, onClose, variant = "permanent" } = props;
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const themeMode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const userQuery = useCurrentUser();
  const inquiryCountQuery = useSubmittedInquiryCount();

  const isPermanent = variant === "permanent";
  // Permanent drawer is an icon rail that expands on hover/focus. Temporary
  // (mobile) drawers always render the full-width expanded layout.
  const [hovered, setHovered] = useState<boolean>(false);
  const expanded = isPermanent ? hovered : true;

  const primary = theme.palette.primary.main;
  const businessName =
    userQuery.data?.business_name !== null &&
    userQuery.data?.business_name !== undefined &&
    userQuery.data.business_name.trim() !== ""
      ? userQuery.data.business_name
      : "Tastefull";
  const logoUrl = userQuery.data?.business_logo_url ?? undefined;

  const handleNavigate = (path: string): void => {
    void navigate(path);
    if (onClose) onClose();
  };

  const handleShowShortcuts = (): void => {
    document.dispatchEvent(new CustomEvent("bakery:show-shortcuts-help"));
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      await useAuthStore.getState().signOut();
      if (onClose) onClose();
      void navigate("/login");
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Sign out failed";
      useToastStore.getState().show({ message, severity: "error" });
    }
  };

  const renderRow = (opts: RowOptions): React.ReactElement => {
    const { icon: Icon, label, onClick, active = false, danger = false, badgeCount = 0 } = opts;
    const button = (
      <ListItemButton
        selected={active}
        onClick={onClick}
        sx={{
          position: "relative",
          minHeight: 48,
          mx: 1,
          my: 0.25,
          borderRadius: 2.5,
          justifyContent: "flex-start",
          // Fixed left inset (mx 4px + pl 24px = 28px) centres the 20px icon in
          // the 76px rail AND keeps it in the exact same spot when expanded, so
          // the icon never shifts/flickers on hover.
          pl: "24px",
          pr: 2.25,
          color: theme.palette.text.secondary,
          transition: theme.transitions.create(["background-color", "color"]),
          "& .MuiListItemIcon-root": { color: "inherit" },
          "&:hover": danger
            ? {
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              }
            : {
                bgcolor: alpha(primary, 0.08),
                color: theme.palette.text.primary,
              },
          "&.Mui-selected": {
            bgcolor: alpha(primary, 0.14),
            color: primary,
            "& .MuiListItemIcon-root": { color: primary },
            "&:hover": { bgcolor: alpha(primary, 0.2) },
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 9,
              bottom: 9,
              width: 4,
              borderRadius: "0 4px 4px 0",
              bgcolor: primary,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: expanded ? 2 : 0,
            justifyContent: "center",
            color: "inherit",
          }}
        >
          <Badge badgeContent={badgeCount} color="error" max={99}>
            <Icon fontSize="small" />
          </Badge>
        </ListItemIcon>
        {/* Only render the label when expanded — an opacity:0 label still
            occupies width and would push the icon off-center in the rail. */}
        {expanded ? (
          <ListItemText
            primary={label}
            sx={{ m: 0 }}
            slotProps={{
              primary: {
                noWrap: true,
                sx: { fontSize: 14, fontWeight: active ? 600 : 500 },
              },
            }}
          />
        ) : null}
      </ListItemButton>
    );
    return (
      <ListItem key={label} disablePadding sx={{ display: "block" }}>
        {expanded ? (
          button
        ) : (
          <Tooltip title={label} placement="right">
            {button}
          </Tooltip>
        )}
      </ListItem>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      {/* Business logo + name */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minHeight: 76,
          justifyContent: "flex-start",
          // (rail 76 - logo 42) / 2 = 17px left inset: centres the logo in the
          // rail and holds it fixed when expanded, so it never shifts.
          pl: "17px",
          pr: 2.25,
        }}
      >
        {/* Shows the uploaded business logo; falls back to a branded tile when
            the logo is absent or the image fails to load (Avatar renders its
            children whenever `src` is missing or errors). */}
        <Avatar
          src={logoUrl}
          variant="rounded"
          alt={businessName}
          slotProps={{ img: { sx: { objectFit: "contain" } } }}
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: 2.5,
            background: `linear-gradient(135deg, ${primary} 0%, ${theme.palette.secondary.main} 100%)`,
            color: "#fff",
            boxShadow: `0 4px 12px ${alpha(primary, 0.4)}`,
          }}
        >
          <BakeryDiningRoundedIcon fontSize="small" />
        </Avatar>
        {/* Name only renders when expanded so the logo centers in the rail. */}
        {expanded ? (
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
              {businessName}
            </Typography>
            <Typography noWrap variant="caption" color="text.secondary">
              Bakery management
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Divider sx={{ mx: 2, mb: 0.5, borderColor: theme.palette.divider }} />

      {/* Wrapper div centers the nav block vertically between the two dividers
          (flex-centering a <ul> directly is unreliable, so center on the Box). */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <List sx={{ py: 0 }}>
          {NAV_ITEMS.map((item) =>
            renderRow({
              icon: item.icon,
              label: item.label,
              active:
                location.pathname === item.path ||
                location.pathname.startsWith(`${item.path}/`),
              onClick: () => {
                handleNavigate(item.path);
              },
              // New-inquiry count on the Inquiries item only.
              badgeCount:
                item.path === "/inquiries" ? (inquiryCountQuery.data ?? 0) : 0,
            }),
          )}
        </List>
      </Box>

      <Divider sx={{ mx: 2, mt: 0.5, borderColor: theme.palette.divider }} />

      <List sx={{ py: 1 }}>
        {renderRow({
          icon: KeyboardIcon,
          label: "Keyboard shortcuts",
          onClick: handleShowShortcuts,
        })}
        {renderRow({
          icon: themeMode === "dark" ? Brightness7Icon : Brightness4Icon,
          label: themeMode === "dark" ? "Light mode" : "Dark mode",
          onClick: toggleTheme,
        })}
        {renderRow({
          icon: LogoutIcon,
          label: "Sign out",
          danger: true,
          onClick: () => {
            void handleSignOut();
          },
        })}
      </List>
    </Box>
  );

  if (variant === "temporary") {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: WIDTH_EXPANDED,
            boxSizing: "border-box",
            borderRadius: 0,
            backgroundImage: "none",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open
      sx={{
        width: WIDTH_RAIL,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: expanded ? WIDTH_EXPANDED : WIDTH_RAIL,
          boxSizing: "border-box",
          borderRadius: 0,
          backgroundImage: "none",
          borderRight: `1px solid ${theme.palette.divider}`,
          overflowX: "hidden",
          zIndex: theme.zIndex.drawer + 1,
          boxShadow: expanded ? theme.shadows[8] : "none",
          transition: theme.transitions.create(["width", "box-shadow"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        },
      }}
      slotProps={{
        paper: {
          onMouseEnter: () => {
            setHovered(true);
          },
          onMouseLeave: () => {
            setHovered(false);
          },
          onFocus: () => {
            setHovered(true);
          },
          onBlur: (event: React.FocusEvent<HTMLDivElement>) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setHovered(false);
            }
          },
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
